import { useEffect, useState } from "react";
import { useT } from "../i18n/index.js";

const TICK_MS = 100;
const URGENT_THRESHOLD_SECONDS = 5;

interface RingState {
  fraction: number;
  seconds: number;
}

function computeRingState(endsAt: number, totalSeconds: number): RingState {
  const remainingMs = Math.max(0, endsAt - Date.now());
  const totalMs = Math.max(1, totalSeconds * 1000);
  return {
    fraction: Math.min(1, remainingMs / totalMs),
    seconds: Math.ceil(remainingMs / 1000),
  };
}

export interface CountdownRingProps {
  /** Epoch ms when the countdown ends. */
  endsAt: number;
  /** Full duration of the countdown, for the ring fraction. */
  totalSeconds: number;
  /** Outer diameter in px. */
  size?: number;
}

/**
 * Animated circular countdown. The ring drains as time runs out and shifts
 * to the error color in the final seconds. The number stays readable when
 * animations are disabled, so reduced-motion users lose nothing.
 */
export function CountdownRing({ endsAt, totalSeconds, size = 56 }: CountdownRingProps) {
  const t = useT();
  const [ring, setRing] = useState<RingState>(() => computeRingState(endsAt, totalSeconds));

  useEffect(() => {
    setRing(computeRingState(endsAt, totalSeconds));
    const interval = window.setInterval(() => {
      setRing(computeRingState(endsAt, totalSeconds));
    }, TICK_MS);
    return () => window.clearInterval(interval);
  }, [endsAt, totalSeconds]);

  const strokeWidth = Math.max(3, size / 14);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const isUrgent = ring.seconds <= URGENT_THRESHOLD_SECONDS;
  const color = isUrgent ? "var(--error)" : "var(--brand)";

  return (
    <div
      className={`countdown-ring${isUrgent ? " is-urgent" : ""}`}
      style={{ width: size, height: size }}
      role="timer"
      // No aria-live: `timer` is already implicitly off, and a label that
      // changes every second would interrupt a screen reader continuously for
      // the whole question. The value stays readable on demand.
      aria-label={t("common.secondsLeft", { seconds: ring.seconds })}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--bg-deep)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - ring.fraction)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: `stroke-dashoffset ${TICK_MS}ms linear, stroke 300ms ease`,
          }}
        />
      </svg>
      <span className="countdown-ring-label" style={{ fontSize: size * 0.32 }}>
        {ring.seconds}
      </span>
    </div>
  );
}
